import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin, CalendarDays } from "lucide-react";
import { Reveal } from "../components/Motion";
import { submitContact, submitBooking } from "../lib/api";
import { BRAND, SERVICES } from "../lib/constants";

const BUDGETS = [
  "< $5,000",
  "$5,000 – $15,000",
  "$15,000 – $50,000",
  "$50,000 – $150,000",
  "$150,000+",
];

export default function Contact() {
  const [contactForm, setContactForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);

  const [booking, setBooking] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    notes: "",
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setContactLoading(true);
    try {
      await submitContact(contactForm);
      toast.success("Inquiry received. We'll respond within one business day.");
      setContactForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (
      !booking.name ||
      !booking.email ||
      !booking.preferred_date ||
      !booking.preferred_time
    ) {
      toast.error("Name, email, date and time are required.");
      return;
    }
    setBookingLoading(true);
    try {
      await submitBooking(booking);
      toast.success("Booking received. Calendar invite incoming.");
      setBooking({
        name: "",
        email: "",
        company: "",
        phone: "",
        preferred_date: "",
        preferred_time: "",
        notes: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Booking failed. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32">
      {/* HERO */}
      <section className="px-6 md:px-8 pb-16">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="overline">Contact</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium leading-[0.95] tracking-tight mt-6 max-w-5xl">
              Let&apos;s <em className="text-[#D4AF37] not-italic">build</em> something
              that compounds.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
              Share what you&apos;re working on. Or skip the form and book a free
              30-minute strategy session directly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="px-6 md:px-8 py-16 md:py-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* LEFT: CONTACT FORM */}
          <Reveal className="lg:col-span-7">
            <div className="border border-white/5 bg-[#0E0E0E] p-8 md:p-12">
              <h2 className="font-display text-3xl md:text-4xl mb-2">
                Project Inquiry
              </h2>
              <p className="text-white/50 text-sm mb-10">
                We respond within one business day.
              </p>

              <form
                onSubmit={handleContactSubmit}
                data-testid="contact-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6"
              >
                <div>
                  <label className="lux-label">Name *</label>
                  <input
                    data-testid="contact-name"
                    className="lux-input"
                    placeholder="Your full name"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="lux-label">Company</label>
                  <input
                    data-testid="contact-company"
                    className="lux-input"
                    placeholder="Your company"
                    value={contactForm.company}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, company: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="lux-label">Email *</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    className="lux-input"
                    placeholder="you@company.com"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="lux-label">Phone</label>
                  <input
                    data-testid="contact-phone"
                    className="lux-input"
                    placeholder="Optional"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="lux-label">Service Needed</label>
                  <select
                    data-testid="contact-service"
                    className="lux-input bg-[#0E0E0E] cursor-pointer"
                    value={contactForm.service}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, service: e.target.value })
                    }
                  >
                    <option value="">Select a service</option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                    <option value="Multiple / Unsure">Multiple / Unsure</option>
                  </select>
                </div>
                <div>
                  <label className="lux-label">Project Budget</label>
                  <select
                    data-testid="contact-budget"
                    className="lux-input bg-[#0E0E0E] cursor-pointer"
                    value={contactForm.budget}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, budget: e.target.value })
                    }
                  >
                    <option value="">Select a range</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="lux-label">Message *</label>
                  <textarea
                    data-testid="contact-message"
                    className="lux-input resize-none"
                    rows="4"
                    placeholder="Tell us about your project, goals, and timeline."
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, message: e.target.value })
                    }
                  />
                </div>

                <div className="md:col-span-2 mt-4">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={contactLoading}
                    className="btn-gold text-sm disabled:opacity-60"
                  >
                    {contactLoading ? "Sending..." : "Submit Inquiry"}
                    <ArrowUpRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          </Reveal>

          {/* RIGHT: INFO + BOOKING */}
          <Reveal delay={0.1} className="lg:col-span-5 space-y-10">
            {/* Strategy Call */}
            <div
              data-testid="booking-card"
              className="border border-[#D4AF37]/20 bg-[#0E0E0E] p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-3">
                <CalendarDays size={18} className="text-[#D4AF37]" strokeWidth={1.6} />
                <span className="overline">Free Strategy Session</span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl leading-tight">
                30 minutes. No pitch. Just clarity.
              </h3>
              <p className="text-white/55 text-sm mt-4 leading-relaxed">
                Book a private call with our team. Walk away with a concrete
                next step — whether we work together or not.
              </p>

              <form
                onSubmit={handleBookingSubmit}
                className="mt-8 grid grid-cols-1 gap-5"
                data-testid="booking-form"
              >
                <div>
                  <label className="lux-label">Name *</label>
                  <input
                    data-testid="booking-name"
                    className="lux-input"
                    placeholder="Your name"
                    value={booking.name}
                    onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="lux-label">Email *</label>
                  <input
                    data-testid="booking-email"
                    type="email"
                    className="lux-input"
                    placeholder="you@company.com"
                    value={booking.email}
                    onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="lux-label">Preferred Date *</label>
                    <input
                      data-testid="booking-date"
                      type="date"
                      className="lux-input"
                      value={booking.preferred_date}
                      onChange={(e) =>
                        setBooking({ ...booking, preferred_date: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="lux-label">Time *</label>
                    <input
                      data-testid="booking-time"
                      type="time"
                      className="lux-input"
                      value={booking.preferred_time}
                      onChange={(e) =>
                        setBooking({ ...booking, preferred_time: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="lux-label">Notes</label>
                  <textarea
                    data-testid="booking-notes"
                    className="lux-input resize-none"
                    rows="2"
                    placeholder="What would you like to discuss?"
                    value={booking.notes}
                    onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={bookingLoading}
                  className="btn-gold text-sm w-fit disabled:opacity-60 mt-2"
                >
                  {bookingLoading ? "Booking..." : "Confirm Booking"}
                  <ArrowUpRight size={16} />
                </button>
              </form>
            </div>

            {/* Contact info */}
            <div data-testid="contact-info" className="space-y-6">
              <div>
                <span className="overline">Reach Us</span>
              </div>
              {[
                { Icon: Mail, label: BRAND.email, href: `mailto:${BRAND.email}`, id: "email" },
                { Icon: Phone, label: BRAND.phone, href: `tel:${BRAND.phone}`, id: "phone" },
                { Icon: MapPin, label: BRAND.address, href: null, id: "address" },
              ].map(({ Icon, label, href, id }) => {
                const Wrapper = href ? "a" : "div";
                return (
                  <Wrapper
                    key={id}
                    href={href}
                    data-testid={`contact-${id}`}
                    className="flex items-center gap-4 border-b border-white/5 pb-4 hover:border-[#D4AF37]/30 transition-colors"
                  >
                    <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                      <Icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                    </div>
                    <span className="text-sm text-white/75">{label}</span>
                  </Wrapper>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
